import { useState, useEffect } from "react";
import {
  Table as CSDTable,
  TextFilter,
  StatusIndicator,
  CollectionPreferencesProps,
} from "@cloudscape-design/components-themed";
import { Link, useNavigate } from "react-router-dom";

import TableEmptyState from "../UI/TableEmptyState";
import TableEditHeader from "../UI/TableEditHeader";
import ActionBtns from "../UI/ActionBtns";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { USER_TYPE } from "../../configs/constants";
import {
  getHeaderCounterText,
  getPagedItems,
  getCodeLabel,
} from "../../utils/common";
import {
  LABELS,
  DISPLAY_NAME,
  EMAIL,
  TYPE,
  COMPANY_NAME,
  STATUS,
  DEACTIVATED_REASON,
  AUTHED_AT,
  MEMO,
  COMMENT,
  statusData,
} from "./constants";
import { COMMON_LABELS } from "../default-constants";
import {
  useGetUsersQuery,
  useUpdateUsersMutation,
  useDeleteUsersMutation,
  useGetAuthQuery,
} from "../../features/api";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { UsersSearchResponse } from "../../features/api-interfaces";
import { getColumnDefinitions } from "./table-config";
import { formatDateTimeWithoutConvert } from "../../utils/format";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { useCodelistHook } from "../../app/useCodelistHook";
import {
  setColumnWidths,
  setPageIndex,
  changePageSize,
} from "../../features/slices/user-list";
import { isEmpty, omit } from "lodash";

interface ITable {
  isBackFromDetail: boolean;
  isSkipSearch: boolean;
}

/**
 * ユーザ一覧 一覧エリア
 */
const Table = (props: ITable) => {
  const { isSkipSearch, isBackFromDetail } = props;
  const dispatch = useAppDispatch();
  const navigator = useNavigate();
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);
  const { companyType } = useCodelistHook();
  const { searchCriteria, tableConfig } = useAppSelector(
    (state) => state.userList
  );
  const { pageSize, pageIndex, columnWidths } = tableConfig;
  const [allItems, setAllItems] = useState<UsersSearchResponse[]>([]);
  const [filteringText, setFilteringText] = useState("");
  const [selectedItems, setSelectedItems] = useState<UsersSearchResponse[]>([]);
  const [pagedItems, setPagedItems] = useState<UsersSearchResponse[]>([]);
  const { cognitoId } = useGetAuthQuery(undefined, {
    selectFromResult: ({ data }) => ({
      cognitoId: data?.username,
    }),
  });
  const hasNoSearchInput = Object.values(
    omit(searchCriteria, ["timestamp"])
  ).every((value) => isEmpty(value));
  const { data, isFetching, refetch } = useGetUsersQuery(searchCriteria, {
    skip: isSkipSearch || hasNoSearchInput,
  });
  const [updateUsers] = useUpdateUsersMutation();
  const [deleteUsers] = useDeleteUsersMutation();
  const columnDefinitions = getColumnDefinitions(isDocomoUser).map(
    (column, index) => {
      const cellRenderers: Record<
        string,
        (item: UsersSearchResponse) => JSX.Element | string | undefined
      > = {
        [DISPLAY_NAME]: (item) => (
          <Link to={`/users/${item.cognitoId}`}>
            {item.familyName + " " + item.givenName}
          </Link>
        ),
        [EMAIL]: (item) => item.email,
        [TYPE]: (item) => getCodeLabel(companyType, String(item.companyType)),
        [COMPANY_NAME]: (item) => item.companyName,
        [STATUS]: (item) =>
          getCodeLabel(statusData, String(item.deactivateFlag)),
        [DEACTIVATED_REASON]: (item) => item.deactivatedReason,
        [AUTHED_AT]: (item) =>
          item.authedAt && (
            <StatusIndicator>
              {formatDateTimeWithoutConvert(item.authedAt)}
            </StatusIndicator>
          ),
        [MEMO]: (item) => item.memo,
        [COMMENT]: (item) => item.comment,
      };

      const cell =
        cellRenderers[column.id] || ((_e: UsersSearchResponse) => <></>);
      const width = isBackFromDetail ? columnWidths[index] : undefined;

      return { ...column, width, cell };
    }
  );
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const isNoneSelected = selectedItems.length === 0;
  const actions = [
    {
      label: COMMON_LABELS.BTNS.ACTIVATE,
      disableFlg: isNoneSelected,
      showModalFlg: true,
      fn: () =>
        updateUsers({
          cognitoIdList: selectedItems.map((i) => i.cognitoId),
          deactivateFlag: false,
        }),
    },
    {
      label: COMMON_LABELS.BTNS.DEACTIVATE,
      disableFlg: isNoneSelected,
      showModalFlg: true,
      fn: () =>
        updateUsers({
          cognitoIdList: selectedItems.map((i) => i.cognitoId),
          deactivateFlag: true,
        }),
    },
    {
      label: COMMON_LABELS.BTNS.DELETE,
      disableFlg: isNoneSelected,
      showModalFlg: true,
      fn: () =>
        deleteUsers({
          cognitoIdList: selectedItems.map((i) => i.cognitoId),
        }),
    },
    {
      label: COMMON_LABELS.BTNS.CREATE,
      showModalFlg: false,
      disableFlg: false,
      fn: () => navigator("/users/signup"),
    },
  ];

  useEffect(() => {
    if (data !== undefined) {
      const newItems = filteringText
        ? data.filter(({ email }) => email.includes(filteringText))
        : data;
      setPagedItems(
        getPagedItems(newItems, pageIndex, pageSize) as UsersSearchResponse[]
      );
      setSelectedItems([]);
      setAllItems(newItems);
    }
  }, [data, pageIndex, pageSize, filteringText]);

  /**
   * メールアドレスで検索した際の入力ハンドラ.
   * @param {string} value 入力値
   */
  const emailSearchOnChange = (value: string) => {
    setFilteringText(value);
    dispatch(setPageIndex(1));
  };

  /**
   * テーブルのカラム幅を変更したときの処理.
   * @param {number[]} widths カラム幅リスト
   */
  const onColumnWidthsChange = (widths: number[]) =>
    dispatch(setColumnWidths(widths));

  /**
   * ページ番号を変更したときの処理.
   * @param {number} currentPageIndex 現在のページ番号
   */
  const setCurrentPageIndex = (currentPageIndex: number) =>
    dispatch(setPageIndex(currentPageIndex));

  /**
   * ページサイズを変更したときの処理.
   * @param {CollectionPreferencesProps.Preferences} detail
   */
  const handleOnConfirm = (detail: CollectionPreferencesProps.Preferences) =>
    dispatch(changePageSize(detail.pageSize!));

  return (
    <CSDTable
      onSelectionChange={({ detail: { selectedItems } }) => {
        setSelectedItems(selectedItems);
      }}
      selectedItems={selectedItems}
      columnDefinitions={columnDefinitions}
      isItemDisabled={(item: UsersSearchResponse) =>
        item.cognitoId === cognitoId
      }
      items={pagedItems}
      onColumnWidthsChange={({ detail: { widths } }) =>
        onColumnWidthsChange(widths as number[])
      }
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      loading={isFetching}
      resizableColumns
      selectionType="multi"
      trackBy="cognitoId"
      empty={<TableEmptyState />}
      filter={
        <TextFilter
          filteringText={filteringText}
          filteringPlaceholder="メールアドレスでユーザを検索"
          onChange={({ detail }) => emailSearchOnChange(detail.filteringText)}
          countText={`${allItems.length} 一致`}
        />
      }
      header={
        <TableEditHeader
          title={LABELS.TABLE.TITLE}
          counter={getHeaderCounterText(allItems.length, pagedItems.length)}
          actions={<ActionBtns actions={actions} refetch={refetch} />}
        />
      }
      pagination={
        <Pagination
          currentPageIndex={pageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={{ pageSize, visibleContent: [] }}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
    />
  );
};

export default Table;
